import { useEffect, useState } from "react";
import { keyProblem, maskKey, tabKeyStore, type StoredKeys } from "../lib/modelKey.js";
import {
  PROVIDERS,
  PROVIDER_IDS,
  loadProvider,
  pickModel,
  rememberModel,
  rememberedModel,
  sendPhotos,
  setSendPhotos,
  type ModelOption,
  type ProviderId,
} from "../lib/modelChoice.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";

/**
 * Settings' home for somebody's own model keys: add or forget one per
 * provider, choose each one's model, and whether photos go. Fill in details
 * only shows its Ask buttons for a provider with a key here.
 *
 * A key is checked by listing the models it may use, and that list is the
 * model menu, so nothing here names a model (`lib/modelChoice.ts`). Listing
 * again on every visit is also what moves somebody off a retired model.
 */
export function ModelKeysPanel() {
  const [keys, setKeys] = useState<StoredKeys>(() => tabKeyStore.read());
  const [lists, setLists] = useState<Partial<Record<ProviderId, ModelOption[]>>>({});
  const [chosen, setChosen] = useState<Partial<Record<ProviderId, string>>>({});
  const [problems, setProblems] = useState<Partial<Record<ProviderId, string>>>({});
  const [adding, setAdding] = useState<ProviderId | null>(null);
  const [draft, setDraft] = useState("");
  const [checking, setChecking] = useState(false);
  const [photos, setPhotos] = useState(sendPhotos);

  const say = (provider: ProviderId, problem: string | null) =>
    setProblems((current) => ({ ...current, [provider]: problem ?? undefined }));

  function takeModels(provider: ProviderId, models: ModelOption[]) {
    setLists((current) => ({ ...current, [provider]: models }));
    const pick = pickModel(models, rememberedModel(provider)?.id ?? null);
    if (pick) {
      rememberModel(provider, pick);
      setChosen((current) => ({ ...current, [provider]: pick.id }));
    }
  }

  // Keys already in this tab: list their models, which also catches a key
  // that has since been revoked and a model that has since been retired.
  useEffect(() => {
    let live = true;
    for (const provider of PROVIDER_IDS) {
      const key = keys[provider];
      if (!key || lists[provider]) continue;
      void loadProvider(provider)
        .then(({ listModels }) => listModels(key))
        .then((answer) => {
          if (!live) return;
          if (answer.ok) takeModels(provider, answer.models);
          else say(provider, answer.reason);
        })
        .catch(() => live && say(provider, "Could not load that. Reload the page and try again."));
    }
    return () => {
      live = false;
    };
    // Only when the set of keys changes; `lists` is filled by this effect.
  }, [keys]);

  async function save(event: React.FormEvent, provider: ProviderId) {
    event.preventDefault();
    const typed = draft.trim();
    const wrong = keyProblem(provider, typed);
    if (wrong) return say(provider, wrong);
    setChecking(true);
    say(provider, null);
    const answer = await loadProvider(provider)
      .then(({ listModels }) => listModels(typed))
      .catch(() => ({ ok: false as const, reason: "Could not load that. Reload the page and try again." }));
    setChecking(false);
    // A key its provider will not take is not kept.
    if (!answer.ok) return say(provider, answer.reason);
    tabKeyStore.save(provider, typed);
    takeModels(provider, answer.models);
    setKeys(tabKeyStore.read());
    setAdding(null);
    setDraft("");
  }

  function forget(provider: ProviderId) {
    tabKeyStore.forget(provider);
    setKeys(tabKeyStore.read());
    setLists((current) => ({ ...current, [provider]: undefined }));
    say(provider, null);
  }

  const anyKey = PROVIDER_IDS.some((provider) => keys[provider]);

  return (
    <Panel title="AI models" note="Fill in item details in one click with your own Claude or Gemini key.">
      <ul className="rows model-keys">
        {PROVIDER_IDS.map((provider) => {
          const info = PROVIDERS[provider];
          const key = keys[provider];
          const models = lists[provider];
          return (
            <li key={provider} className="model-key">
              <div className="row-item">
                <span className="row-icon">
                  <Icon name="key" size={18} />
                </span>
                <span className="row-text">
                  <strong>{info.assistant}</strong>
                  <span className="muted small mono">{key ? maskKey(key) : "No key"}</span>
                </span>
                {key && models && (
                  <select
                    className="input model-key-model"
                    aria-label={`${info.assistant} model`}
                    value={chosen[provider] ?? ""}
                    onChange={(event) => {
                      const model = models.find((option) => option.id === event.target.value);
                      if (!model) return;
                      rememberModel(provider, model);
                      setChosen((current) => ({ ...current, [provider]: model.id }));
                    }}
                  >
                    {models.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                )}
                {key ? (
                  <button
                    className="button button-ghost button-small"
                    type="button"
                    onClick={() => forget(provider)}
                  >
                    Forget
                  </button>
                ) : (
                  adding !== provider && (
                    <button
                      className="button button-quiet button-small"
                      type="button"
                      onClick={() => {
                        setAdding(provider);
                        setDraft("");
                        say(provider, null);
                      }}
                    >
                      Add key
                    </button>
                  )
                )}
              </div>

              {adding === provider && !key && (
                <form className="model-key-form" onSubmit={(event) => void save(event, provider)}>
                  <div className="inline-add inline-add-wrap">
                    <input
                      className="input mono"
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      aria-label={`${info.company} API key`}
                      placeholder={info.keyHint}
                      value={draft}
                      disabled={checking}
                      autoFocus
                      onChange={(event) => {
                        setDraft(event.target.value);
                        say(provider, null);
                      }}
                    />
                    <button className="button" type="submit" disabled={checking}>
                      {checking ? "Checking…" : "Save"}
                    </button>
                    <button className="button button-ghost" type="button" onClick={() => setAdding(null)}>
                      Cancel
                    </button>
                  </div>
                  {provider === "google" && (
                    <p className="muted small">
                      On Gemini's free tier, Google may use what you send to improve its products.
                    </p>
                  )}
                </form>
              )}

              {problems[provider] && <Notice tone="error">{problems[provider]}</Notice>}
            </li>
          );
        })}
      </ul>

      {anyKey && (
        <label className="toggle">
          <input
            type="checkbox"
            checked={photos}
            onChange={(event) => {
              setSendPhotos(event.target.checked);
              setPhotos(event.target.checked);
            }}
          />
          <span className="toggle-track" aria-hidden="true" />
          <span>
            Send photos
            <span className="toggle-hint">Up to 3 per item.</span>
          </span>
        </label>
      )}

      <p className="muted small">
        Keys last until this tab closes or you sign out, and go only to their provider, who bills you.
      </p>
    </Panel>
  );
}
